<template>
  <CModal
    v-if="berth"
    :title="modalTitle"
    color="success"
    size="lg"
    :show="show"
    :centered="true"
    @update:show="handleUpdateShow">
    <div class="content">
      <h2 class="mb-3">Berth Details</h2>

      <template v-if="berthErrors.length">
        <CAlert
          v-for="(error, index) in berthErrors"
          :key="`error-${index}`"
          color="danger"
          :show="10"
          closeButton>
          {{ error }}
        </CAlert>
      </template>

      <CRow>
        <CCol col="6" class="pr-3">
          <div class="details-row">
            <div class="details-row__title">Id</div>
            <div class="details-row__value">{{ berthInternal.berthId }}</div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Name</div>
            <div class="details-row__value">
              <editable-field
                fieldType="text"
                fieldName="berthName"
                :entityData="berthInternal"
                :saveMethod="updateBerth"
                @updated="berthUpdated"
                @update-error="berthUpdateError"
                placeholder="--"/>
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Type</div>
            <div class="details-row__value">
              <editable-field
                fieldType="select"
                fieldName="berthType"
                :items="berthTypeChoices"
                :entityData="berthInternal"
                :saveMethod="updateBerth"
                @updated="berthUpdated"
                @update-error="berthUpdateError"
                placeholder="--"/>
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Company</div>
            <div class="details-row__value">
              <editable-field
                fieldType="text"
                fieldName="company"
                :entityData="berthInternal"
                :saveMethod="updateBerth"
                @updated="berthUpdated"
                @update-error="berthUpdateError"
                placeholder="--"/>
            </div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Flow Direction</div>
            <div class="details-row__value">
              <editable-field
                fieldType="select"
                fieldName="flowDirection"
                :items="directionChoices"
                :entityData="berthInternal"
                :saveMethod="updateBerth"
                @updated="berthUpdated"
                @update-error="berthUpdateError"
                placeholder="--"/>
            </div>
          </div>
        </CCol>
        <CCol col="6" class="pl-3">
          <div class="section-title">Notes</div>

          <editable-field
            fieldType="textarea"
            fieldName="notes"
            :entityData="berthInternal"
            :saveMethod="updateBerth"
            @updated="berthUpdated"
            @update-error="berthUpdateError"
            placeholder="- empty -"/>
        </CCol>
      </CRow>

      <div class="commodities-header">
        <h2 class="title">Commodities</h2>
        <div class="actions">
          <i
            class="fa fa-2x fa-plus add-berth"
            title="Add berth commodity"
            @click="handleAddBerthCommodity" />
        </div>
      </div>

      <CDataTable
        :items="formattedBerthCommodities"
        :fields="berthCommodities.fields"
        hover
        :responsive="true">
        <template #masterdataValidated="{item}">
          <td v-if="item.masterdataValidated">
            <CBadge color="success">True</CBadge>
          </td>
          <td v-else><CBadge color="secondary">False</CBadge></td>
        </template>
        <template #validatedBr="{item}">
          <td v-if="item.validatedBr"><CBadge color="success">True</CBadge></td>
          <td v-else><CBadge color="secondary">False</CBadge></td>
        </template>
        <template #validatedUb="{item}">
          <td v-if="item.validatedUb"><CBadge color="success">True</CBadge></td>
          <td v-else><CBadge color="secondary">False</CBadge></td>
        </template>
        <template #validatedJl="{item}">
          <td v-if="item.validatedJl"><CBadge color="success">True</CBadge></td>
          <td v-else><CBadge color="secondary">False</CBadge></td>
        </template>
        <template #validatedWb="{item}">
          <td v-if="item.validatedWb"><CBadge color="success">True</CBadge></td>
          <td v-else><CBadge color="secondary">False</CBadge></td>
        </template>
        <template #validations="{item}">
          <td v-if="item.validations">{{ item.validations }}</td>
          <td v-else>0</td>
        </template>
        <template #actions="{item}">
          <td class="py-2 text-center">
            <CButton
              color="danger"
              variant="outline"
              square
              size="sm"
              @click="handleCommodityRemove(item)">
              Remove
            </CButton>
          </td>
        </template>
      </CDataTable>

      <!-- commodity add modal -->
      <berth-commodity-add-modal
        @updated="handleCommodityAdded"
        :berth-id="berthInternal.berthId"
        :show.sync="addCommodityModalShow"/>

      <!-- commodity remove modal -->
      <berth-commodity-remove-modal
        @updated="handleCommodityRemoved"
        :berth="berthInternal"
        :commodity="selectedCommodity"
        :show.sync="removeCommodityModalShow"/>
    </div>

    <template #footer>
      <CButton @click="handleUpdateShow(false)" color="secondary">Close</CButton>
    </template>
  </CModal>
</template>

<script>
import { mapActions } from "vuex";
import BerthCommodityAddModal from "./BerthCommodityAddModal";
import BerthCommodityRemoveModal from "./BerthCommodityRemoveModal";
import EditableField from "../../widgets/EditableField";

export default {
  name: "berth-edit-modal",

  components: {
    EditableField,
    BerthCommodityAddModal,
    BerthCommodityRemoveModal,
  },

  props: {
    berth: {
      type: Object,
      default: () => null,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const actionsField = {
      key: "actions",
      label: "Actions",
      sorter: false,
      _classes: "text-center",
      filter: false,
    };

    return {
      berthInternal: null,

      berthCommodities: {
        fields: [
          "commodityName",
          "flowDirection",
          "masterdataValidated",
          "validations",
          "validatedWb",
          "validatedUb",
          "validatedJl",
          "validatedBr",
          "source",
          "updatedAt",

          actionsField,
        ],
        items: [],
        currentId: null,
        showModal: false,
      },
      commodities: [],

      addCommodityModalShow: false,
      removeCommodityModalShow: false,
      berthErrors: [],
    };
  },

  methods: {
    ...mapActions("polygons", [
      "listCommodities",
      "listBerthCommodities",
      "updateBerth",
    ]),

    async loadCommodities() {
      this.berthCommodities.items = await this.listBerthCommodities(
        this.berth.berthId
      );
      let commodities = await this.listCommodities(); // commodities are cached in store.state
      this.commodities = {};
      commodities.forEach((item) => {
        this.commodities[item.value] = {
          id: item.value,
          commodityName: item.text,
          commodityGroup: item.group,
        };
      });
    },

    handleUpdateShow(value) {
      this.$emit("update:show", value);
    },

    handleAddBerthCommodity() {
      this.addCommodityModalShow = true;
    },

    async handleCommodityAdded() {
      await this.loadCommodities();
    },

    handleCommodityRemove(commodity) {
      // opens remove dialog
      this.berthCommodities.currentId = commodity.commodityId;
      this.removeCommodityModalShow = true;
    },

    async handleCommodityRemoved() {
      // updates list when a commodity was removed
      this.removeCommodityModalShow = false;
      this.berthCommodities.currentId = null;
      await this.loadCommodities();
    },

    berthUpdated(data) {
      this.berthInternal = data;
      this.$emit("updated", data);
    },

    berthUpdateError(response) {
      this.berthErrors = [];
      if (response.error_details) {
        this.berthErrors.push(response.error_details);
      } else {
        this.berthErrors.push("Unknown berth update error");
      }
    },
  },

  computed: {
    modalTitle() {
      if (this.berth) {
        return `Berth edit: "${this.berth.berthName}"`;
      } else {
        return `Berth edit`;
      }
    },

    selectedCommodity() {
      return (
        this.formattedBerthCommodities.find((item) => {
          return item.commodityId === this.berthCommodities.currentId;
        }) || null
      );
    },

    formattedBerthCommodities() {
      return this.berthCommodities.items.map((item) => {
        return {
          ...item,
          ...this.commodities[item.commodityId],
        };
      });
    },

    directionChoices() {
      return [
        { value: "IMPORT", label: "IMPORT" },
        { value: "EXPORT", label: "EXPORT" },
        { value: "BOTH", label: "BOTH" },
      ];
    },
    berthTypeChoices() {
      return [
        { value: "BERTH", label: "BERTH" },
        { value: "LOADING_ANCHORAGE", label: "LOADING_ANCHORAGE" },
      ];
    },
  },

  watch: {
    async show(value) {
      if (value) {
        this.berthInternal = this.berth;
        await this.loadCommodities();
      } else {
        this.berthInternal = {};
      }
    },
  },
};
</script>

<style scoped></style>

<style scoped lang="scss">
.content {
  padding: 0 10px;
}
.section-title {
  margin-bottom: 0.7rem;
  font-size: 16px;
  border-bottom: 1px solid #b2b2b2;
}

.details-row {
  padding-bottom: 5px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dotted #d0d0d0;

  &__title {
    min-width: 50px;
    padding-right: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__value {
    padding-left: 10px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    /*direction: rtl;*/
    /*text-align: left;*/
  }
}

.commodities-header {
  margin: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  .title {
    margin: 0;
  }
  .actions {
    .add-berth {
      cursor: pointer;
      color: #008e00;
    }
    .add-berth:hover {
      color: #00b400;
    }
  }
}
</style>
